<template>
  <div
    v-show="isActive"
    id="geoprocessingID"
    class="map-tool-window geoprocessing-tool-window"
  >
    <!-- Header -->
    <div
      id="geoprocessingIDheader"
      class="map-tool-window-header"
    >
      <p class="map-tool-window-header-title">
        Ejecutar geoproceso
      </p>

      <div class="map-tool-window-header-actions">
        <button
          type="button"
          class="control-btn"
          @click="isHidden = !isHidden"
        >
          <svg
            v-if="isHidden"
            viewBox="0 0 24 24"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>

          <svg
            v-else
            viewBox="0 0 24 24"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        <button
          type="button"
          class="control-btn"
          @click="exitGeoprocessing()"
        >
          <svg viewBox="0 0 24 24">
            <line x1="5" y1="5" x2="19" y2="19" />
            <line x1="19" y1="5" x2="5" y2="19" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Indicador de carga del geoproceso. Va fuera del body para que siga
         visible aunque el usuario minimice la ventana, y fijo al viewport
         porque el spinner del botón "Ejecutar" pasa desapercibido. -->
    <div
      v-if="loading"
      class="geoprocessing-progress"
    >
      <font-awesome-icon icon="spinner" spin />

      <div class="geoprocessing-progress-text">
        <strong>Geoproceso en curso</strong>
        <span>{{ progressMessage }}</span>
      </div>
    </div>

    <!-- Body -->
    <div
      v-show="!isHidden"
      class="map-tool-window-body geoprocessing-tool-body"
    >
      <!-- Selección del geoproceso -->
      <div class="geoprocessing-control">
        <label for="geoprocessSelect">
          Geoproceso
        </label>

        <select
          id="geoprocessSelect"
          v-model="selectedGeoprocessId"
        >
          <option disabled value="">
            Seleccionar geoproceso...
          </option>

          <option
            v-for="option in geoprocessingsOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>

      <!-- Formulario dinámico -->
      <div
        v-if="activeFormComponent"
        class="geoprocessing-form-container"
      >
        <component
          :is="activeFormComponent"
          @submit="handleSubmit"
          @changeDrawType="onChangeDrawType"
          @generateRings="onGenerateRings"
          @generatePlume="onGeneratePlume"
          @updateGeoprocessingBody="onUpdateGeoprocessingBody"
        />
      </div>


      <div
        v-else
        class="geoprocessing-empty-state"
      >
        <p>Selecciona un geoproceso para configurar sus parámetros.</p>
      </div>

      <!-- Estado de la pluma en tiempo real (independiente del botón Ejecutar) -->
      <div
        v-if="selectedGeoprocessId === 'PlumaDispersion' && plumeStatusMessage"
        class="plume-status"
        :class="'plume-status--' + plumeState"
      >
        <font-awesome-icon v-if="plumeState === 'loading'" icon="spinner" spin />
        {{ plumeStatusMessage }}
      </div>

      <!-- Acciones -->
      <div class="geoprocessing-actions">
        <button
          type="button"
          class="geoprocessing-btn geoprocessing-btn--secondary"
          @click="cleanMode()"
        >
          Limpiar
        </button>

        <button
          type="button"
          class="geoprocessing-btn geoprocessing-btn--primary"
          :disabled="loading || !canExecuteGeoprocessing"
          :title="executeButtonTitle"
          @click="executeGeoprocessingVue()"
        >
          <font-awesome-icon
            v-if="loading"
            icon="spinner"
            spin
          />

          {{ loading ? 'Procesando...' : 'Ejecutar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { draggableDiv } from "@/components/mixin/draggableDiv.js";
import { vuex } from "@/components/mixin/vuex.js";
import { geoprocessingsInteraction } from "./geoprocessingsInteraction.js";
import { mapService } from "@/services/map/mapService";
import InfluenciaRadialForm from "./forms/InfluenciaRadialForm.vue";
import InfluenciaLinealForm from "./forms/InfluenciaLinealForm.vue";
import InfluenciaPoligonalForm from "./forms/InfluenciaPoligonalForm.vue";
import PlumaDispersionForm from "./forms/PlumaDispersionForm.vue";
import GeoJSON from 'ol/format/GeoJSON';
import Circle from 'ol/geom/Circle';
import { fromCircle } from 'ol/geom/Polygon';
import { LayerService } from '@/services/layers/layerService';
import { geoprocessingSelectionHandler } from '@/components/mapTools/geoprocessings/utils/GeoprocessingSelectionHandler';
import { normalizeGeoprocessingGeometry } from '@/components/mapTools/geoprocessings/utils/geoprocessingGeometry';
import {
  buildVulnerableCatalog,
  filterVulnerableLayers,
  isPopulationLayer,
} from '@/components/mapTools/geoprocessings/utils/vulnerableLayers';

export default {
  name: "HelloWorld",
  props: ["isFixed"],
  mixins: [draggableDiv, vuex, geoprocessingsInteraction],
  data() {
    return {
      errorMessage: "",
      selectedGeoprocessId: "",
      chartData: null,
      geoprocessingsOptions: [
        { id: "InfluenciaRadial", name: "Influencia Radial" },
        { id: "InfluenciaLineal", name: "Influencia Lineal" },
        { id: "InfluenciaPoligonal", name: "Influencia Poligonal" },
        { id: "PlumaDispersion", name: "Pluma de Dispersión" },
      ],

      // ✅ solo para leer lo que emite el hijo
      formDataFromChild: null,

      geoprocessingBody: {
        layers: [],
        geometry: {
          coordinates: [],
          type: "Polygon",
        },
        radius: 1,
      },
      geoRequestData: null,

      geoprocessingUrl: "-1",
      drawSelected: { name: "Polígono", code: "Polygon" },
      suspendedGeoDraw: false,
      draws: [
        { name: "Polígono", code: "Polygon" },
        { name: "Círculo", code: "Circle" },
      ],

      isHidden: false,
      loading: false,
      progressMessage: "",
      plumeState: "", // "", "loading", "ok", "error" — estado de la pluma en tiempo real
    };
  },
  computed: {
    isActive() {
      return this.isFixed
        ? this.isMobile
        : this.mapToolValues("geoprocessing").active && !this.isMobile;
    },
    selectedGeoprocess() {
      return this.geoprocessingsOptions.find((g) => g.id === this.selectedGeoprocessId);
    },
    requiresRadiusValidation() {
      return ["InfluenciaRadial", "InfluenciaLineal"].includes(this.selectedGeoprocessId);
    },
    canExecuteGeoprocessing() {
      if (!this.activeFormComponent) return false;
      if (!this.requiresRadiusValidation) return true;
      return Boolean(this.formDataFromChild && this.formDataFromChild.radiosValidos);
    },
    executeButtonTitle() {
      if (this.loading) return "Procesando geoproceso";
      if (this.requiresRadiusValidation && !this.canExecuteGeoprocessing) {
        return (
          (this.formDataFromChild && this.formDataFromChild.mensajeValidacionRadios) ||
          "Complete los radios en orden creciente para ejecutar."
        );
      }
      return "Ejecutar geoproceso";
    },
    activeFormComponent() {
      console.log("Form seleccionado:", this.selectedGeoprocessId);
      switch (this.selectedGeoprocessId) {
        case "InfluenciaRadial":
          return InfluenciaRadialForm;
        case "InfluenciaLineal":
          return InfluenciaLinealForm;
        case "InfluenciaPoligonal":
          return InfluenciaPoligonalForm;
        case "PlumaDispersion":
          return PlumaDispersionForm;
        default:
          return null;
      }
    },
    // Mensaje de estado de la pluma en tiempo real (independiente del botón
    // "Ejecutar"): dice qué está pasando por detrás en vez de un genérico.
    plumeStatusMessage() {
      switch (this.plumeState) {
        case "loading":
          return "Generando pluma…";
        case "ok":
          return "Pluma actualizada.";
        case "error":
          return this.errorMessage || "No se pudo generar la pluma.";
        default:
          return "";
      }
    },
  },
  methods: {
    // ✅ guardamos lo que manda el hijo (sin cambiar tu flujo)
    onUpdateGeoprocessingBody(newBody) {
      console.log("📬 GeoprocessingBody recibido:", newBody);
      this.geoprocessingBody = newBody; // <- lo tuyo original
      this.formDataFromChild = newBody; // <- solo guardamos para usar en payload
    },

    cleanMode() {
      this.executeMapToolAction("geoprocessing", "clean", {});
      this.cleanGeoDraw();
      geoprocessingSelectionHandler.clearHighlight();
      this.chartData = null;
    },
    exitGeoprocessing() {
      this.cleanResults();
      this.selectMapTool("geoprocessing", false);
      this.errorMessage = "";
    },

    async executeGeoprocessingVue() {
      if (
        this.requiresRadiusValidation &&
        !(this.formDataFromChild && this.formDataFromChild.radiosValidos)
      ) {
        this.$store.dispatch("toast/show", {
          message: (
            (this.formDataFromChild && this.formDataFromChild.mensajeValidacionRadios) ||
            "Los radios deben ser mayores que cero y cumplir R1 < R2 < R3."
          ),
          type: "warning"
        });
        return;
      }

      this.loading = true;
      this.progressMessage = "Obteniendo capas…";
      try {
        console.log("Ejecutando geoproceso...");
        // 🔄 Limpiar resultados previos para dar feedback visual de "procesando"
        this.cleanResults();

        let layersToProcess = [];
        const mode = (this.formDataFromChild && this.formDataFromChild.capas) || "cargadas";
        // Esta consulta es la fuente de verdad para todos los modos. De este
        // modo una capa recién habilitada entra inmediatamente y una que dejó
        // de ser vulnerable ya no puede procesarse desde la selección o mapa.
        const vulnerableLayers = await LayerService.getVulnerableLayers();

        if (mode === "cargadas") {
          layersToProcess = filterVulnerableLayers(this.activeLayers, vulnerableLayers);
        } else if (mode === "seleccionar") {
          // Use specifically selected IDs from the modal
          const ids = (this.formDataFromChild && this.formDataFromChild.capasSeleccionadas) || [];

          const selectedLayers = ids.map(id => {
            // Se resuelve contra la consulta actual y no contra el store: una
            // capa recién creada puede procesarse aun si el árbol del mapa fue
            // cargado antes de que el administrador la habilitara.
            const found = vulnerableLayers.find(l => l.id == id);
            if (found) return found;
            console.warn(`⚠️ Capa con ID ${id} ya no está habilitada como vulnerable.`);
            return null;
          }).filter(l => l !== null);
          layersToProcess = filterVulnerableLayers(selectedLayers, vulnerableLayers);
        }

        // Las capas censales se consultan separadamente mediante
        // `capas_poblacion`; no deben entrar también como infraestructura.
        const resultLayers = layersToProcess.filter(layer => !isPopulationLayer(layer));

        // Se conserva el catálogo exacto usado en esta ejecución para que el
        // informe describa el mismo conjunto aunque el administrador cambie
        // la configuración antes de que el usuario lo descargue.
        const vulnerableCatalog = buildVulnerableCatalog(resultLayers, vulnerableLayers);

        console.log(`Modo de capas: ${mode}, Capas vulnerables: ${layersToProcess.length}, capas de resultado: ${resultLayers.length}`);

        if (layersToProcess.length === 0) {
          this.$store.dispatch("toast/show", { message: "No hay capas seleccionadas/activas para procesar.", type: "warning" });
          return;
        }

        console.log("🧱 geoRequestData actual:", this.geoRequestData);
        console.log("📝 FormData From Child:", this.formDataFromChild);

        let finalInputGeometries = [];
        const usingLayerSelection = this.formDataFromChild && this.formDataFromChild.otrasCapas;

        // 1. Check for Selection from Child (Highest Priority)
        if (this.formDataFromChild && this.formDataFromChild.seleccionInfo && this.formDataFromChild.seleccionInfo.features) {
          console.log("🛠️ Using features from Selection Handler");
          const format = new GeoJSON();
          const features = this.formDataFromChild.seleccionInfo.features;

          // Filter features based on Process Type
          if (this.selectedGeoprocessId === 'InfluenciaRadial') {
            // For Radial, we must buffer the Points (Centers) by the requested proper radii
            // because the backend expects Polygons with a radius property, identical to drawn circles.
            const pointFeatures = features.filter(f => f.getGeometry().getType() === 'Point');

            // Get requested radii from form data
            const radiuses = (this.formDataFromChild.distancias) || [1]; // default 1km if missing

            pointFeatures.forEach(f => {
              const center = f.getGeometry().getCoordinates();
              radiuses.forEach(distM => {
                const radiusMeters = distM;
                const circleGeom = new Circle(center, radiusMeters);
                const polygonGeom = fromCircle(circleGeom);

                finalInputGeometries.push({
                  type: "Polygon",
                  coordinates: polygonGeom.getCoordinates(),
                  radius: radiusMeters
                });
              });
            });
          } else if (this.selectedGeoprocessId === 'InfluenciaLineal') {
            // El handler conserva las líneas seleccionadas para resaltarlas y
            // agrega los polígonos de influencia como elementos separados.
            // Sólo esos buffers deben enviarse como geometrías de análisis.
            const bufferFeatures = features.filter(
              f => f.get('geoprocessingRole') === 'line-buffer'
            );

            finalInputGeometries = bufferFeatures.map(f => ({
              type: f.getGeometry().getType(),
              coordinates: f.getGeometry().getCoordinates(),
              radius: f.get('radius')
            }));
          } else {
            // For other types, pass geometry as is
            finalInputGeometries = features.map(f => normalizeGeoprocessingGeometry(
              format.writeGeometryObject(f.getGeometry())
            ));
          }
        }
        else if (usingLayerSelection) {
          // User checked "Other Layers" but no selection info is available
          this.$store.dispatch("toast/show", { message: "Ha seleccionado 'Usar elementos de otras capas' pero no ha seleccionado ningún elemento. Use el botón 'Seleccione elementos' y dibuje un área sobre los puntos deseados.", type: "warning" });
          return;
        }
        // 2. Fallback to Standard Drawing (Only if NOT using layer selection)
        else if (this.geoRequestData && this.geoRequestData.input_geometries) {
          finalInputGeometries = this.geoRequestData.input_geometries;
        }

        if (!finalInputGeometries || finalInputGeometries.length === 0) {
          this.$store.dispatch("toast/show", { message: "No hay geometría generada aún. Dibuje un área o seleccione elementos.", type: "warning" });
          return;
        }

        const layers = resultLayers.map((l) => (
          console.log("🚀 Capa y su campos", l.campos),
          {
            id: l.id,
            name: l.name,
            source: l.source,
            type: l.type,
            geometry_type: l.geometry_type || l.geometryType || l.type || '',
            provider: l.provider,
            codigo: l.codigo, // Required for ArcGIS sublayer identification
            campos: l.campos,
            id_category: l.id_category,
            category_name: l.category_name,
            cap_poblacion: l.cap_poblacion || l.capPoblacion || '',
          }));
        console.log("🚀 Capas", layers);

        // ✅ Standardized Payload Construction
        const method = (this.formDataFromChild && this.formDataFromChild.metodo) || "polygon";
        console.log("🚀 Obteniendo capas de población...", this.formDataFromChild.metodo);
        const capasPoblacion = await LayerService.getPoblationLayers(this.formDataFromChild.metodo);

        if (capasPoblacion.length === 0) {
          const tipoPoblacion = method === "point" ? "puntos" : "polígonos";
          this.$store.dispatch("toast/show", {
            message: `No existen capas registradas para usar población como ${tipoPoblacion}.`,
            type: "warning"
          });
          return;
        }

        const payload = {
          layers: layers,
          input_geometries: finalInputGeometries,
          process_type: this.selectedGeoprocessId,
          calculation_method: method,
          capas_poblacion: capasPoblacion,
          dissolve: (this.formDataFromChild && this.formDataFromChild.dissolve) || false,
          ...(this.formDataFromChild && this.formDataFromChild.distancias ? { radiuses: this.formDataFromChild.distancias } : {}),
        };

        console.log("🚀 Enviando payload al backend:", payload);

        // Etapa larga: el servicio resuelve todas las capas contra sus
        // proveedores, por lo que el usuario queda acá la mayor parte del tiempo.
        this.progressMessage = `Analizando ${layers.length} ${layers.length === 1 ? 'capa' : 'capas'}…`;

        const url = process.env.VUE_APP_API_GEOPROCESS + "execute"; // TODO : cambiar al URL real
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        const responseJson = await response.json();
        console.log("✅ Respuesta recibida del backend:", responseJson);
        responseJson["result"].vulnerable_catalog = vulnerableCatalog;
        this.progressMessage = "Dibujando resultados…";
        const graphResponse = responseJson["result"]["results_graphs"];
        const rawResults = responseJson["result"]["results"] || [];

        // Extract unique layers from results
        const uniqueLayers = [];
        const seenIds = new Set();

        rawResults.forEach(item => {
          if (item.layer && !seenIds.has(item.layer.id)) {
            uniqueLayers.push(item.layer);
            seenIds.add(item.layer.id);
          }
        });

        // Attach layers to graphResponse to access them in the Chart component
        graphResponse.availableLayers = uniqueLayers;
        graphResponse.rawResults = rawResults;
        graphResponse.results_table = responseJson["result"]["results_table"] || {};

        // Pasamos la geometría de filtro (priorizamos la que viene del backend, que puede estar disuelta)
        const responseGeometries = responseJson["result"]["input_geometries"] || [];
        if (responseGeometries.length > 0) {
          graphResponse.filterGeometry = responseGeometries[0];
          // El informe HTML necesita todas las zonas (p. ej. pluma con 3
          // anillos); filterGeometry se mantiene con [0] para el filtro CQL.
          // freeze: solo lo consume el payload del informe, sin reactividad
          graphResponse.filterGeometries = Object.freeze(responseGeometries);

          // Si se aplicó dissolve, actualizamos las marcas visuales en el mapa para mostrar el área unificada
          if (this.formDataFromChild && this.formDataFromChild.dissolve) {
             const format = new GeoJSON();
             const dissolvedFeatures = responseGeometries.map(g => format.readFeature({
                type: 'Feature',
                geometry: g,
                properties: {}
             }));
             // Actualizamos el SelectionHandler para que muestre solo la geometría disuelta
             geoprocessingSelectionHandler.highlightFeatures(dissolvedFeatures, false);
          }
        } else if (finalInputGeometries && finalInputGeometries.length > 0) {
          graphResponse.filterGeometry = finalInputGeometries[0];
          graphResponse.filterGeometries = Object.freeze(finalInputGeometries);
        }

        graphResponse.populationLayers = capasPoblacion;
        graphResponse.populationMethod = method;

        if (this.selectedGeoprocessId === "PlumaDispersion" && this.formDataFromChild && this.formDataFromChild.pluma) {
          graphResponse.plumeParams = this.formDataFromChild.pluma;
        }

        // Add Population Graphs if available
        if (responseJson["result"]["population_graphs"]) {
          graphResponse.population = responseJson["result"]["population_graphs"];
        }

        // Pass PDF Data
        if (responseJson["result"]["pdf_report"]) {
          graphResponse.pdfReport = responseJson["result"]["pdf_report"];
        }

        console.log("📈 Respuesta del geoproceso:", graphResponse);
        this.chartData = graphResponse;

        // Emitir resultado para que otros componentes (ej. GeoprocessingChart) lo tomen via Vuex o evento global
        this.executeMapToolAction("geoprocessing", "showResults", {
          chartData: graphResponse,
          reportPayload: responseJson["result"],
          timestamp: Date.now()
        });
        this.$emit("geoprocessing-result", graphResponse);

      } catch (error) {
        console.error("Error al ejecutar el geoproceso:", error);
        this.$store.dispatch("toast/show", { message: "Ocurrió un error al ejecutar el geoproceso.", type: "error" });
      } finally {
        this.loading = false;
        this.progressMessage = "";
      }
    },

    showResults() {
      this.executeMapToolAction("geoprocessing", "showResults", {});
    },
    cleanResults() {
      this.errorMessage = "";
      this.executeMapToolAction("geoprocessing", "cleanResults", {});
    },
    showError(error) {
      this.errorMessage = error;
    },
    drawChange() {
      this.executeMapToolAction("geoprocessing", "changeDrawType", {
        drawType: this.drawSelected,
      });
    },

    // ✅ opcional: cuando el hijo hace submit explícito
    handleSubmit(formData) {
      console.log("handleSubmit formData:", formData);
      this.formDataFromChild = formData; // solo almacenamos
    },

    onChangeDrawType(drawType) {
      // El @mousedown.capture del panel no dispara con Enter/Espacio en el control.
      this.claimMapInteraction("geoprocessing");
      this.executeMapToolAction("geoprocessing", "clean", {});
      this.addGeoInteraction(drawType);
      this.drawSelected = drawType;
      this.executeMapToolAction("geoprocessing", "changeDrawType", { drawType });
    },
    onGenerateRings({ distances }) {
      console.log("📏 Dibujar radios en mapa:", distances);
      const cantidadEsperada = Number(
        this.formDataFromChild && this.formDataFromChild.radioSeleccionado
      );
      if (
        !Array.isArray(distances) ||
        distances.length === 0 ||
        (cantidadEsperada && distances.length !== cantidadEsperada)
      ) {
        this.$store.dispatch("toast/show", { message: "Debe ingresar al menos un radio válido.", type: "warning" });
        return;
      }
      for (let i = 1; i < distances.length; i++) {
        if (distances[i] <= distances[i - 1]) {
          return;
        }
      }
      console.log("✅ Radios válidos, dibujando en mapa...");
      if (this.selectedGeoprocessId === "InfluenciaLineal") {
        // En modo capa existente la previsualización la administra el handler
        // sobre las líneas seleccionadas; no existe `lastGeometry` dibujada.
        if (!(this.formDataFromChild && this.formDataFromChild.otrasCapas)) {
          this.drawLineBuffers(distances);
        }
        return;
      }

      this.drawRings(distances);
    },
    async onGeneratePlume({ hours, windDirection, windSpeed }) {
      const center = this.getCenter();
      // En modo tiempo real esto se dispara en cada cambio de parámetro; si aún
      // no hay punto de origen simplemente no generamos, sin molestar con un
      // alert (el botón "Ubique un punto" es el llamado a la acción visible).
      if (!center) {
        return;
      }

      console.log("💨 Generando pluma:", { hours, windDirection, windSpeed });
      // Estado propio de la pluma: NO tocamos this.loading (ése es del botón
      // "Ejecutar", el análisis pesado). Generar la pluma sólo dibuja su
      // geometría; el análisis nunca se dispara acá.
      this.plumeState = "loading";
      // Modo tiempo real: cambios rápidos disparan varias peticiones. Marcamos
      // cada una y sólo dibujamos/actualizamos si sigue siendo la más reciente,
      // así una respuesta lenta que llega tarde no pisa una pluma más nueva.
      const reqId = (this._plumeReqId = (this._plumeReqId || 0) + 1);
      // El servicio es remoto (VPN): si no responde, abortamos para no quedar
      // colgados en "Generando…" para siempre.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000);
      try {
        const url = process.env.VUE_APP_API_GEOPROCESS + "plume";
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            x: center[0],
            y: center[1],
            hours: hours,
            wind_direction: windDirection,
            wind_speed_kmh: windSpeed,
          }),
          signal: controller.signal,
        });
        const responseJson = await response.json();
        if (reqId !== this._plumeReqId) return; // respuesta obsoleta, la ignoramos

        if (!response.ok) {
          this.errorMessage = responseJson.detail || "No se pudo generar la pluma.";
          this.plumeState = "error";
          console.warn("No se pudo generar la pluma:", this.errorMessage);
          return;
        }

        this.drawPlume(responseJson.input_geometries);
        this.plumeState = "ok";
      } catch (error) {
        if (reqId !== this._plumeReqId) return; // ya hay una petición más nueva
        console.warn("No se pudo generar la pluma:", error);
        this.errorMessage = "No se pudo generar la pluma. Revisa la conexión con el servicio (VPN).";
        this.plumeState = "error";
      } finally {
        clearTimeout(timeoutId);
      }
    },
    // Reúne los parámetros vigentes del formulario y dispara la generación.
    // Se usa cuando el punto de origen se ubica después de fijar parámetros.
    autoGeneratePlume() {
      const p = this.formDataFromChild && this.formDataFromChild.pluma;
      if (!p) return;
      // Misma validación que scheduleGeneratePlume del formulario: con el
      // input a medio editar (v-model.number deja '') no se dispara la request
      if (!(p.tiempo >= 1) || !(p.velocidad >= 1)) return;
      this.onGeneratePlume({
        hours: p.tiempo,
        windDirection: p.direccion,
        windSpeed: p.velocidad,
      });
    },
  },
  mounted() {
    this.makeDraggable("geoprocessingID");
    this.map = mapService.get();
  },
  beforeDestroy() {
    this.cleanResults();
    this.removeGeoInteraction();
    this.selectMapTool("geoprocessing", false);
  },
  watch: {
    mapInteractionTool: function (tool) {
      if (!this.isActive) return;
      if (tool === 'geoprocessing') {
        if (!this.suspendedGeoDraw) return;
        this.suspendedGeoDraw = false;
        this.addGeoInteraction(this.drawSelected);
        return;
      }
      // Suspender de nuevo devuelve false porque el draw ya se quitó; la bandera
      // debe sobrevivir a varios cambios de prioridad seguidos.
      this.suspendedGeoDraw = this.suspendGeoInteraction() || this.suspendedGeoDraw;
    },
    mapToolExecuted: function (newValue, oldValue) {
      switch (newValue) {
        case "geoprocessing":
          const action = this.mapToolValues("geoprocessing").action;
          const data = this.mapToolValues("geoprocessing").data;

          if (action === "sendError") {
            this.showError(data.errorMessage);
          }

          if (action === "ready") {
            console.log(
              "📨 Recibido geoRequestData desde requests:",
              data.geoRequestData
            );
            this.geoRequestData = JSON.parse(JSON.stringify(data.geoRequestData)); // ✅ copia real, no referencia
            console.log("🧱 geoRequestData guardado en componente:", this.geoRequestData);

            // Pluma en tiempo real: al ubicar el punto de origen la generamos de
            // inmediato, sin esperar un cambio de parámetro. El guard por tipo
            // "Point" evita reentrar cuando drawPlume vuelve a emitir 'ready'
            // con los polígonos resultantes.
            const geoms = this.geoRequestData && this.geoRequestData.input_geometries;
            if (
              this.selectedGeoprocessId === "PlumaDispersion" &&
              geoms && geoms[0] && geoms[0].type === "Point"
            ) {
              this.autoGeneratePlume();
            }

            // Influencia radial en tiempo real: si los radios se configuraron
            // antes de ubicar el punto, se generan al terminar el dibujo.
            // El guard por Point evita reentrar cuando drawRings emite los
            // polígonos resultantes.
            if (
              this.selectedGeoprocessId === "InfluenciaRadial" &&
              !(this.formDataFromChild && this.formDataFromChild.otrasCapas) &&
              geoms && geoms[0] && geoms[0].type === "Point"
            ) {
              const formData = this.formDataFromChild;
              if (formData && formData.radiosValidos) {
                this.onGenerateRings({ distances: [...formData.distancias] });
              }
            }

            // Influencia lineal en tiempo real: al terminar la polilínea se
            // generan inmediatamente los buffers configurados. El guard por
            // LineString evita reentrar cuando drawLineBuffers emite los
            // polígonos resultantes.
            if (
              this.selectedGeoprocessId === "InfluenciaLineal" &&
              !(this.formDataFromChild && this.formDataFromChild.otrasCapas) &&
              geoms && geoms[0] && geoms[0].type === "LineString"
            ) {
              const distances = (
                (this.formDataFromChild && this.formDataFromChild.distancias) || []
              ).filter((value) => Number(value) > 0);

              if (distances.length > 0) {
                this.onGenerateRings({ distances });
              }
            }
          }
          break;

        default:
          break;
      }
    },
    // ✅ Limpiar mapa al cambiar de geoproceso
    selectedGeoprocessId: function (val) {
      console.log("🔄 Cambio de geoproceso, limpiando mapa...");
      this.plumeState = "";
      this.cleanMode();
      this.removeGeoInteraction();
    },
    // ✅ Limpiar interacciones de dibujo si el usuario cambia a modo "Usar capa existente"
    'formDataFromChild.otrasCapas': function (val) {
      if (val) {
        console.log("🔄 Cambio a modo capa, removiendo interacciones de dibujo...");
        this.removeGeoInteraction();
      }
    },
    'formDataFromChild.dissolve': function (val) {
      console.log("🔄 Cambio en Dissolve:", val);
      geoprocessingSelectionHandler.updateOptions({ dissolve: val });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.plume-status {
  margin: 6px 0;
  font-size: 0.8rem;
  color: var(--color-navy, #004b7a);
}
.plume-status--error {
  color: #d9342b;
}
.plume-status--ok {
  color: #2e7d32;
}
.geoprocessing-progress {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);

  z-index: 10001;

  display: flex;
  align-items: center;
  gap: 14px;

  padding: 16px 26px;

  /* Fondo sólido en vez de blanco: QA-127 pedía más contraste para que no
     pase desapercibido sobre un mapa con muchos colores. */
  background: var(--color-navy, #0e2a40);
  border-radius: 10px;
  box-shadow: 0 10px 32px rgba(14, 42, 64, 0.4);

  color: var(--color-white, #fff);
  font-size: 1rem;

  svg {
    color: var(--color-orange, #e8730c);
    font-size: 1.6rem;
  }
}

.geoprocessing-progress-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.35;

  strong {
    font-size: 1.05rem;
  }

  span {
    color: var(--color-fog, #d7dee5);
    font-size: 0.88rem;
  }
}

.geoprocessing-tool-window {
  top: 4em;
  left: 16px;

  width: 400px;
  max-width: calc(100vw - 32px);

  z-index: 1000;
}



.geoprocessing-tool-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}



.geoprocessing-control {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    margin: 0;

    color: var(--color-navy);
    font-size: 0.82rem;
    font-weight: 700;
  }

  select {
    width: 100%;
    min-height: 38px;
    box-sizing: border-box;

    padding: 8px 10px;

    background: var(--color-white);
    border: 1px solid var(--color-line);
    border-radius: 4px;

    color: var(--color-graphite);
    font-family: inherit;
    font-size: 0.85rem;

    cursor: pointer;

    &:hover {
      border-color: var(--color-steel);
    }

    &:focus {
      border-color: var(--color-steel);
      outline: 2px solid var(--color-fog);
      outline-offset: 1px;
    }
  }
}



.geoprocessing-form-container {
  padding: 14px;

  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: 4px;
}



.geoprocessing-empty-state {
  padding: 18px 14px;

  background: var(--color-mist);
  border: 1px dashed var(--color-line);
  border-radius: 4px;

  text-align: center;

  p {
    margin: 0;

    color: var(--color-ghost);
    font-size: 0.8rem;
    line-height: 1.45;
  }
}



.geoprocessing-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  padding-top: 14px;
  border-top: 1px solid var(--color-line);
}

.geoprocessing-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  min-width: 100px;
  min-height: 36px;
  padding: 8px 14px;

  border-radius: 4px;

  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:focus-visible {
    outline: 2px solid var(--color-steel);
    outline-offset: 2px;
  }
}

.geoprocessing-btn--secondary {
  background: var(--color-white);
  border: 1px solid var(--color-line);

  color: var(--color-graphite);

  &:hover {
    background: var(--color-fog);
    border-color: var(--color-steel);
    color: var(--color-navy);
  }
}


.geoprocessing-btn--primary {
  background: var(--color-navy);
  border: 1px solid var(--color-navy);

  color: var(--color-white);

  &:hover:not(:disabled) {
    background: var(--color-steel);
    border-color: var(--color-steel);
  }

  &:disabled {
    background: var(--color-fog);
    border-color: var(--color-line);

    color: var(--color-ghost);

    cursor: not-allowed;
  }
}
</style>
